// noinspection DuplicatedCode

import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    Signal,
    signal,
    untracked,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslocoService } from '@jsverse/transloco';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { EditWarnMessageComponent } from '@connect-services/client/components/edit-warn-message/edit-warn-message.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AddressBANResult, MapSearchService } from '@connect-services/client/services/map-search.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { ProjectStore } from '@connect-services/client/stores/project.store';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { AddressBANFeature, Layer, SearchEntity } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EditWarnMessageComponent, FormsModule, MatAutocompleteModule, RessourcesModule, SelectComponent],
    selector: 'app-global-search',
    styleUrl: './global-search.component.scss',
    templateUrl: './global-search.component.html',
})
export class GlobalSearchComponent {
    public readonly autocompleteTrigger: Signal<MatAutocompleteTrigger> =
        viewChild.required<MatAutocompleteTrigger>('autoInput');
    public banLayerName = 'searchAddressBAN';
    public readonly banSearchResults: WritableSignal<AddressBANResult[] | undefined> = signal(undefined);
    public readonly isEditMode = computed(() => this.projectEditModeService.isEditModeActive());
    public readonly isLoading = signal(true);
    public readonly labelKey = input<string>('search');
    public readonly layerNames: Signal<Set<string>> = computed(
        () => new Set(this.projectStore.layerList()?.map((layer: Layer) => layer.name) ?? []),
    );
    public readonly layerNamesOptions: WritableSignal<SelectOption<string>[]> = signal<SelectOption<string>[]>([]);
    public readonly previousSearch = signal('');
    public readonly projectSearchResults: WritableSignal<SearchEntity[] | undefined> = signal(undefined);
    public readonly searchValue = signal<string>('');
    public readonly selectedLayersOptions: WritableSignal<SelectOption<string>[]> = signal([]);
    public readonly showLabel = input<boolean>(true);

    protected readonly debounceDuration = 1000;
    protected debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    protected readonly maxSearchLength = 50;
    protected readonly minSearchLength = 3;

    private readonly destroyRef = inject(DestroyRef);
    private readonly hasBanAccess = signal(false);
    private readonly mapSearchService = inject(MapSearchService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly projectStore = inject(ProjectStore);
    private readonly subscriptionService = inject(SubscriptionService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        effect(() => {
            this.projectBloc.value();
            this.resetSearch();
        });

        effect(async () => {
            if (this.projectStore.loading()) {
                this.isLoading.set(true);

                return;
            }

            const layerNames = Array.from(this.layerNames());
            const userSubscription = await untracked(() => this.subscriptionService.getSubscription());

            if (userSubscription.options.hasBanAccess) {
                const banLayerName = this.translocoService.translate(this.banLayerName);
                layerNames.unshift(banLayerName);
            }

            this.hasBanAccess.set(userSubscription.options.hasBanAccess);

            this.layerNamesOptions.set(
                layerNames.map((layerName: string) => {
                    return { label: layerName, value: layerName };
                }),
            );
            this.isLoading.set(false);
        });

        effect(() => {
            if (!this.mapSearchService.selectedSearchItem()) {
                this.resetSearch();
            }
        });

        this.destroyRef.onDestroy(() => this.resetSearch());
    }

    public onSearch(isNewLayersSelection: boolean = false): void {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
        }

        this.debounceTimeout = setTimeout(async () => {
            const searchVal = this.searchValue();
            const search =
                searchVal.length < this.minSearchLength || searchVal.length > this.maxSearchLength ? '' : searchVal;

            if (searchVal.length >= 1) {
                this.autocompleteTrigger().openPanel();
            }

            if (this.previousSearch() === search && !isNewLayersSelection) {
                return;
            }

            this.previousSearch.set(search);

            if (!search.length) {
                this.banSearchResults.set(undefined);
                this.projectSearchResults.set(undefined);

                return;
            }

            await this.getSearchMapData();
        }, this.debounceDuration);
    }

    public resetSearch(): void {
        this.searchValue.set('');

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
        }

        this.projectSearchResults.set(undefined);
        this.banSearchResults.set(undefined);
        this.mapSearchService.selectedAddressItem.set(undefined);
        this.previousSearch.set('');
    }

    public selectDataEntitySearchItem(searchResultData: SearchEntity): void {
        this.mapSearchService.selectedSearchItem.set({
            ...searchResultData,
        });
    }

    public selectedAddressSearchItem(feature: AddressBANResult): void {
        this.mapSearchService.selectedAddressItem.set(feature);
    }

    public setResultsPanelPosition(): void {
        const pane = document.querySelector('.cdk-overlay-pane') as HTMLElement;

        if (!pane) {
            return;
        }

        // Needs the timeout or does not run
        setTimeout(() => {
            const currentTop = parseInt(pane.style.top, 10);
            pane.style.top = `${currentTop + 8}px`;
        }, 0);
    }

    private async getSearchMapData(): Promise<void> {
        this.banSearchResults.set(undefined);
        this.projectSearchResults.set(undefined);

        const layerNames = this.selectedLayersOptions().map((layerOption) => layerOption.value);

        const banLayerName = this.translocoService.translate(this.banLayerName);
        const index = layerNames.indexOf(banLayerName);

        if ((!layerNames.length || index !== -1) && this.hasBanAccess()) {
            await this.setBanAddressSearchResults();
            layerNames.splice(index, 1);
        }

        if (layerNames.length || (!layerNames.length && index === -1)) {
            this.projectSearchResults.set(
                await this.mapSearchService.getSearchData(this.projectBloc.value()!.id, this.searchValue(), layerNames),
            );
        }
    }

    private async setBanAddressSearchResults(): Promise<void> {
        const features: AddressBANFeature[] = await this.mapSearchService.getAddressBAN(
            this.projectBloc.value()!.id,
            this.searchValue(),
        );
        this.banSearchResults.set(
            features
                .filter((feature: AddressBANFeature) => !!feature.properties)
                .map((feature: AddressBANFeature) => {
                    AssertService.assertPointType(feature.geometry);

                    return {
                        geometry: feature.geometry,
                        id: feature.properties!.id,
                        label: feature.properties!.label,
                    };
                }),
        );
    }
}
